// Procedural canvas textures: nothing is downloaded, so the scene can never render blank
// because of a missing asset.
import * as THREE from 'three';

/** Small deterministic PRNG so textures look the same on every load. */
export function rng(seed = 1) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function canvas(w, h) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d')];
}

function finish(tex, { srgb = true, repeat = false, anisotropy = 8 } = {}) {
  if (srgb) tex.colorSpace = THREE.SRGBColorSpace;
  if (repeat) tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = anisotropy;
  tex.needsUpdate = true;
  return tex;
}

/** Tileable grass detail: thousands of short blade strokes in varied greens. */
export function grassTexture() {
  const size = 512;
  const [c, g] = canvas(size, size);
  const rand = rng(7);
  g.fillStyle = '#3c8a3a';
  g.fillRect(0, 0, size, size);
  for (let i = 0; i < 18000; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const l = 2 + rand() * 5;
    const a = -Math.PI / 2 + (rand() - 0.5) * 0.9;
    const shade = 0.75 + rand() * 0.5;
    g.strokeStyle = `rgba(${(50 * shade) | 0},${(130 * shade) | 0},${(48 * shade) | 0},${0.35 + rand() * 0.4})`;
    g.lineWidth = 0.6 + rand() * 0.9;
    // strokes near an edge are drawn again on the opposite side so the tile repeats seamlessly
    const ox = x < 8 ? size : x > size - 8 ? -size : 0;
    const oy = y < 8 ? size : y > size - 8 ? -size : 0;
    g.beginPath();
    for (const [px, py] of [[x, y], [x + ox, y], [x, y + oy], [x + ox, y + oy]]) {
      g.moveTo(px, py);
      g.lineTo(px + Math.cos(a) * l, py + Math.sin(a) * l);
    }
    g.stroke();
  }
  // soft blotches to break up repetition
  for (let i = 0; i < 40; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const r = 20 + rand() * 60;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    const dark = rand() > 0.5;
    grd.addColorStop(0, dark ? 'rgba(20,60,20,0.10)' : 'rgba(140,200,90,0.07)');
    grd.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grd;
    g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  return finish(new THREE.CanvasTexture(c), { repeat: true, anisotropy: 16 });
}

/**
 * Traditional 32-panel football (truncated icosahedron): black pentagons, white hexagons and
 * stitched seams, computed per texel from the panel centres so it maps exactly onto a SphereGeometry.
 */
export function ballTextures() {
  const W = 512;
  const H = 256;
  const phi = (1 + Math.sqrt(5)) / 2;
  const pent = [];
  for (const a of [-1, 1]) for (const b of [-1, 1]) {
    pent.push([0, a, b * phi], [a, b * phi, 0], [b * phi, 0, a]);
  }
  const hex = [];
  for (const a of [-1, 1]) for (const b of [-1, 1]) for (const c of [-1, 1]) hex.push([a, b, c]);
  for (const a of [-1, 1]) for (const b of [-1, 1]) {
    hex.push([0, a / phi, b * phi], [a / phi, b * phi, 0], [b * phi, 0, a / phi]);
  }
  const norm = (v) => { const l = Math.hypot(...v); return v.map((x) => x / l); };
  const centres = [...pent.map((v) => ({ d: norm(v), p: true })), ...hex.map((v) => ({ d: norm(v), p: false }))];

  const [c, g] = canvas(W, H);
  const [bc, bg] = canvas(W, H);
  const img = g.createImageData(W, H);
  const bump = bg.createImageData(W, H);
  for (let y = 0; y < H; y++) {
    const theta = ((y + 0.5) / H) * Math.PI;
    const st = Math.sin(theta);
    const ct = Math.cos(theta);
    for (let x = 0; x < W; x++) {
      const ph = ((x + 0.5) / W) * Math.PI * 2;
      // same parameterisation as THREE.SphereGeometry
      const dx = -Math.cos(ph) * st;
      const dy = ct;
      const dz = Math.sin(ph) * st;
      let best = -2;
      let second = -2;
      let bestP = false;
      for (const cc of centres) {
        // pentagons are slightly smaller panels on a real ball
        const dot = cc.d[0] * dx + cc.d[1] * dy + cc.d[2] * dz + (cc.p ? -0.012 : 0);
        if (dot > best) { second = best; best = dot; bestP = cc.p; } else if (dot > second) second = dot;
      }
      const edge = best - second; // ~0 on a seam
      const seam = Math.min(1, edge / 0.012);
      const puff = Math.min(1, edge / 0.09); // panel bulge for the bump map
      let r, gg, b;
      if (bestP) { r = 24; gg = 26; b = 32; } else { r = 246; gg = 246; b = 242; }
      const s = 0.35 + 0.65 * seam;
      r = r * s + 40 * (1 - s); gg = gg * s + 40 * (1 - s); b = b * s + 44 * (1 - s);
      const i = (y * W + x) * 4;
      img.data[i] = r; img.data[i + 1] = gg; img.data[i + 2] = b; img.data[i + 3] = 255;
      const bv = 255 * (0.25 + 0.75 * Math.sqrt(puff));
      bump.data[i] = bump.data[i + 1] = bump.data[i + 2] = bv; bump.data[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  bg.putImageData(bump, 0, 0);
  return {
    map: finish(new THREE.CanvasTexture(c)),
    bump: finish(new THREE.CanvasTexture(bc), { srgb: false }),
  };
}

/** Diamond-mesh goal net tile (white strings on transparent). */
export function netTexture() {
  const s = 64;
  const [c, g] = canvas(s, s);
  g.clearRect(0, 0, s, s);
  g.strokeStyle = 'rgba(255,255,255,0.95)';
  g.lineWidth = 3;
  g.beginPath();
  g.moveTo(0, 0); g.lineTo(s, s);
  g.moveTo(s, 0); g.lineTo(0, s);
  g.stroke();
  // knots
  g.fillStyle = '#fff';
  for (const [x, y] of [[0, 0], [s, 0], [0, s], [s, s], [s / 2, s / 2]]) { g.beginPath(); g.arc(x, y, 2.6, 0, 7); g.fill(); }
  return finish(new THREE.CanvasTexture(c), { repeat: true });
}

/** Scrolling LED advertising board strip with original branding. */
export function boardTexture() {
  const W = 4096;
  const H = 128;
  const [c, g] = canvas(W, H);
  const items = [
    ['PENALTY KINGS 3D', '#0b0f1e', '#d8fd71'],
    ['start.ng', '#d8fd71', '#10120c'],
    ['TOP BINS ONLY', '#11203f', '#ffffff'],
    ['PLAY  •  SCORE  •  REPEAT', '#e8313f', '#ffffff'],
    ['start.ng', '#0b0f1e', '#d8fd71'],
    ['KEEP YOUR NERVE', '#18c7d8', '#06222a'],
  ];
  const seg = W / items.length;
  items.forEach(([text, bg, fg], i) => {
    g.fillStyle = bg;
    g.fillRect(i * seg, 0, seg, H);
    g.fillStyle = fg;
    let size = 72;
    g.font = `900 italic ${size}px "Segoe UI", Arial, sans-serif`;
    const w = g.measureText(text).width;
    if (w > seg * 0.86) { size = Math.floor((size * seg * 0.86) / w); g.font = `900 italic ${size}px "Segoe UI", Arial, sans-serif`; }
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(text, i * seg + seg / 2, H / 2 + 3);
  });
  // LED pixel grid
  g.fillStyle = 'rgba(0,0,0,0.22)';
  for (let x = 0; x < W; x += 4) g.fillRect(x, 0, 1, H);
  for (let y = 0; y < H; y += 4) g.fillRect(0, y, W, 1);
  return finish(new THREE.CanvasTexture(c), { repeat: true });
}

/** Radial glow used for floodlight flares. */
export function glowTexture() {
  const s = 128;
  const [c, g] = canvas(s, s);
  const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.18, 'rgba(255,250,235,0.85)');
  grd.addColorStop(0.45, 'rgba(255,240,210,0.22)');
  grd.addColorStop(1, 'rgba(255,240,210,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, s, s);
  return finish(new THREE.CanvasTexture(c));
}

/** Floodlight lamp bank: a grid of bright lamps. */
export function lampTexture() {
  const [c, g] = canvas(256, 160);
  g.fillStyle = '#222630';
  g.fillRect(0, 0, 256, 160);
  for (let y = 0; y < 4; y++) for (let x = 0; x < 6; x++) {
    const cx = 24 + x * 42;
    const cy = 22 + y * 38;
    const grd = g.createRadialGradient(cx, cy, 0, cx, cy, 18);
    grd.addColorStop(0, '#ffffff');
    grd.addColorStop(0.6, '#fff6dc');
    grd.addColorStop(1, '#9b927c');
    g.fillStyle = grd;
    g.beginPath(); g.arc(cx, cy, 16, 0, 7); g.fill();
  }
  return finish(new THREE.CanvasTexture(c));
}

/** Keeper jersey with a chevron pattern and a number on the chest. */
export function jerseyTexture(shirt, trim) {
  const [c, g] = canvas(256, 256);
  g.fillStyle = shirt;
  g.fillRect(0, 0, 256, 256);
  g.fillStyle = trim;
  g.globalAlpha = 0.28;
  for (let i = -4; i < 10; i++) {
    g.beginPath();
    g.moveTo(0, i * 32); g.lineTo(128, i * 32 + 40); g.lineTo(256, i * 32);
    g.lineTo(256, i * 32 + 10); g.lineTo(128, i * 32 + 50); g.lineTo(0, i * 32 + 10);
    g.fill();
  }
  g.globalAlpha = 1;
  g.fillRect(0, 0, 256, 14); // collar band
  g.font = '900 70px "Segoe UI", Arial, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillStyle = trim;
  g.fillText('1', 128, 128);
  return finish(new THREE.CanvasTexture(c), { anisotropy: 4 });
}
